function requiredText(value,label,maxLength){
  const text=String(value||'').trim();
  if(!text)throw new TypeError(`${label} is required`);
  if(text.length>maxLength)throw new TypeError(`${label} is too long`);
  return text;
}

function optionalText(value,maxLength,label='Text value'){
  if(value==null||value==='')return null;
  const text=String(value).trim();
  if(text.length>maxLength)throw new TypeError(label+' is too long');
  return text||null;
}

function stableKey(value,label){
  const key=String(value||'').trim().toLowerCase();
  if(!/^[a-z][a-z0-9_]{1,63}$/.test(key))throw new TypeError(`Invalid ${label}`);
  return key;
}

function generatedId(value,label='id'){
  const id=String(value||'').trim();
  if(id)return id;
  const generated=globalThis.crypto?.randomUUID?.();
  if(generated)return generated;
  throw new TypeError(`${label} is required when crypto.randomUUID is unavailable`);
}

export function createThermometer({
  id,
  userId,
  name,
  typeKey='other',
  vendor=null,
  model=null,
  channelCount=null,
  archivedAt=null
}={}){
  const channels=channelCount==null||channelCount===''?null:Number(channelCount);
  if(channels!==null&&(!Number.isInteger(channels)||channels<1||channels>64)){
    throw new TypeError('channelCount must be an integer between 1 and 64');
  }

  return Object.freeze({
    id:generatedId(id),
    userId:requiredText(userId,'userId',128),
    name:requiredText(name,'name',100),
    typeKey:stableKey(typeKey,'typeKey'),
    vendor:optionalText(vendor,100,'vendor'),
    model:optionalText(model,100,'model'),
    channelCount:channels,
    archivedAt:archivedAt?String(archivedAt):null
  });
}

export function updateThermometer(thermometer,changes={}){
  const current=createThermometer(thermometer);
  return createThermometer({...current,...changes,id:current.id,userId:current.userId});
}

export function archiveThermometer(thermometer,{now=new Date().toISOString()}={}){
  return updateThermometer(thermometer,{archivedAt:now});
}

export function thermometerToRow(value){
  const t=createThermometer(value);
  return Object.freeze({
    id:t.id,
    user_id:t.userId,
    name:t.name,
    type_key:t.typeKey,
    vendor:t.vendor,
    model:t.model,
    channel_count:t.channelCount,
    archived_at:t.archivedAt
  });
}

export function thermometerFromRow(row={}){
  return createThermometer({
    id:row.id,
    userId:row.user_id,
    name:row.name,
    typeKey:row.type_key,
    vendor:row.vendor,
    model:row.model,
    channelCount:row.channel_count,
    archivedAt:row.archived_at
  });
}

export const ProbePurpose=Object.freeze({
  UNKNOWN:'unknown',
  AMBIENT:'ambient',
  PRODUCT:'product',
  OTHER:'other'
});

const PURPOSES=new Set(Object.values(ProbePurpose));

export function createProbe({
  id,
  thermometerId,
  label,
  purpose=ProbePurpose.UNKNOWN,
  channelIndex=null,
  inactiveAt=null
}={}){
  const channel=channelIndex==null||channelIndex===''?null:Number(channelIndex);
  if(channel!==null&&(!Number.isInteger(channel)||channel<1||channel>64)){
    throw new TypeError('channelIndex must be an integer between 1 and 64');
  }
  const resolvedPurpose=String(purpose||'').trim().toLowerCase();
  if(!PURPOSES.has(resolvedPurpose))throw new TypeError('Unsupported probe purpose');

  return Object.freeze({
    id:generatedId(id,'probe id'),
    thermometerId:requiredText(thermometerId,'thermometerId',128),
    label:requiredText(label,'label',100),
    purpose:resolvedPurpose,
    channelIndex:channel,
    inactiveAt:inactiveAt?String(inactiveAt):null
  });
}

export function updateProbe(probe,changes={}){
  const current=createProbe(probe);
  return createProbe({...current,...changes,id:current.id,thermometerId:current.thermometerId});
}

export function deactivateProbe(probe,{now=new Date().toISOString()}={}){
  return updateProbe(probe,{inactiveAt:now});
}

// Capture the user-facing probe configuration at the moment a historical
// reading/session references it. Later edits or deactivation must not change
// what that historical record says the probe was called or used for.
export function probeSnapshot(probe){
  const p=createProbe(probe);
  return Object.freeze({
    id:p.id,
    thermometerId:p.thermometerId,
    label:p.label,
    purpose:p.purpose,
    channelIndex:p.channelIndex
  });
}

export function probeToRow(value){
  const p=createProbe(value);
  return Object.freeze({
    id:p.id,
    thermometer_id:p.thermometerId,
    label:p.label,
    purpose:p.purpose,
    channel_index:p.channelIndex,
    inactive_at:p.inactiveAt
  });
}

export function probeFromRow(row={}){
  return createProbe({
    id:row.id,
    thermometerId:row.thermometer_id,
    label:row.label,
    purpose:row.purpose,
    channelIndex:row.channel_index,
    inactiveAt:row.inactive_at
  });
}
